import React from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../../lib/api';
import { communicationSecretConsentStatusKey } from '../../hooks/api';
import { getOnboardingData, confirmAndLinkFamily } from '../../lib/onboardingSession';
import liffManager from '../../lib/liff';
import { sanitizeReturnTo } from '../../lib/safePath';
import CommunicationSecretConsent from './components/CommunicationSecretConsent';

interface LocationState {
  /**
   * 招待経由（LinkToken）でこの画面に到達した場合の情報。
   * これらが揃っている時は同意保存後に家族連携（confirmAndLinkFamily）まで行う。
   */
  relationship?: string;
  patientId?: number;
  patientName?: string;
  companyName?: string;
  /**
   * Gate 強制フローであることを示す明示フラグ。
   * `CommunicationConsentGate` から遷移する場合のみ `true` を積み、
   * 招待経由 onboarding フロー（LinkToken からの遷移）では積まない。
   * `returnTo` の有無だけで判定すると、onboarding 側が返り先を `state` に載せた
   * 途端に Gate 経路と誤判定されるリスクがある（PR-3 レビュー critical 指摘）。
   */
  isGate?: boolean;
  /**
   * Gate 強制遷移の戻り先（`/consent` 保存後に navigate する URL）。
   * `isGate === true` の場合にのみ意味を持つ。onboarding フローでは参照されない。
   */
  returnTo?: string;
}

/**
 * 通信の秘密同意画面（電気通信事業法 第4条 / LINE アカウント単位）。
 *
 * 2 つの導線をこのページで扱う（設計書 §4.1 / §4.2）:
 *
 * 1. **招待経由の onboarding**: LinkToken から state.relationship / patientId を受け取り、
 *    同意保存後に confirmAndLinkFamily → プロフィール判定 → home へ遷移
 * 2. **Gate 強制**: 既存家族が version_major up 後の初アクセス時に
 *    CommunicationConsentGate から強制遷移。state.returnTo に元の遷移先を保持し、
 *    同意保存後にそこへ戻す。家族連携は不要（既に登録済み）
 */
export const ConsentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const state = location.state as LocationState | null;

  // 認証チェックは親側の AppReadyGate(authOnlySkipConsent) が担う（PR-3 レビュー指摘）。
  // 従来 useLineLoginGuard で LineOpenPrompt を出していた分岐は AuthGate の
  // /link_token リダイレクトに一本化されたため、本コンポーネントは認証済み前提の
  // ロジックのみを担当する。

  // Gate 強制フロー識別: `CommunicationConsentGate` が積む明示フラグ `isGate` で判定する。
  // `returnTo` の有無で判定すると、onboarding フローが返り先を state に載せた
  // 途端に Gate 経路と誤判定される（PR-3 レビュー第 4 巡 critical 指摘）。
  // 別セッションの stale onboardingData（例: 前の招待でクリアされずに sessionStorage に
  // 残っている）を誤って参照して confirmAndLinkFamily を古いトークンで再実行しない
  // 目的は、この明示フラグで安全に達成できる。
  const isGateFlow = state?.isGate === true;

  // 招待経由 onboarding フローの識別:
  // Gate 経路では絶対に発火させない（isGateFlow を否定する）ため、
  // 「returnTo なし & onboardingData 存在 & relationship / patientId 揃い」の 3 条件で成立。
  const onboardingData = isGateFlow ? null : getOnboardingData();
  const relationship = isGateFlow
    ? undefined
    : state?.relationship || onboardingData?.relationship;
  const patientId = isGateFlow
    ? undefined
    : state?.patientId ?? onboardingData?.patientId;
  const patientName = isGateFlow
    ? undefined
    : state?.patientName || onboardingData?.patientName;

  // 家族連携完了後（confirmAndLinkFamily で onboardingData がクリア済み）に、
  // ブラウザ/LIFF WebView の bfcache 復帰などで location.state だけが残ったまま
  // `/consent` に再到達するケースがある。この場合 isOnboardingFlow=true と誤判定して
  // 同意 POST 後に confirmAndLinkFamily が「オンボーディングデータが見つかりません」で
  // throw し、通信の秘密同意が意図せず重複保存されてしまうため、sessionStorage を
  // 真実性チェックの根拠にして stale state を先に排除する（PR-3 レビュー指摘）。
  // Gate 強制経由（isGateFlow=true）は本判定を通らないため returnTo 戻り挙動は維持される。
  if (
    !isGateFlow
    && !onboardingData
    && (state?.patientId != null || state?.relationship != null)
  ) {
    return <Navigate to="/" replace />;
  }

  const isOnboardingFlow =
    !isGateFlow && onboardingData != null && relationship != null && patientId != null;

  // 中途半端 state 検知: Gate 経由でも onboarding 経由でもないケース（sessionStorage の
  // 部分破損 / 将来の実装変更 / onboardingData 存在するが relationship か patientId が
  // 欠落等）は、同意 POST 後に confirmAndLinkFamily が呼ばれず家族連携未完了のまま
  // home に無言遷移するリスクがある。旧 Consent.tsx にあった防御を isGate 導入後の
  // 形で復元し、招待リンクからやり直させる（PR-3 レビュー nit 指摘）。
  if (!isGateFlow && !isOnboardingFlow) {
    return <Navigate to="/link_token" replace />;
  }

  const handleComplete = async ({
    consentDate,
    versionMajor,
    versionMinor,
  }: {
    consentDate: string;
    versionMajor: number;
    versionMinor: number;
  }) => {
    // 通信の秘密同意ステータスの react-query キャッシュを楽観更新する。
    // これをしないと `useCommunicationSecretConsentStatus` の staleTime (60_000ms)
    // 内は古い `has_current: false` が読まれ、CommunicationConsentGate が /consent へ
    // 再度強制リダイレクトして無限ループになる（PR-3 レビュー critical 指摘 / 主要ユース
    // ケース = 既存家族の Gate 経路そのもの）。POST レスポンスの version を反映して
    // 追加の BE 往復なしで整合させる。
    queryClient.setQueryData(communicationSecretConsentStatusKey, {
      has_current: true,
      current_version: { major: versionMajor, minor: versionMinor },
      consented_version: { major: versionMajor, minor: versionMinor },
      consent_date: consentDate,
    });

    // Gate 強制フロー: onboarding 情報が無い場合は returnTo（or /）に戻すだけ。
    // returnTo は sanitize して protocol-relative URL / 外部パスを弾く。
    if (!isOnboardingFlow) {
      navigate(sanitizeReturnTo(state?.returnTo), { replace: true });
      return;
    }

    // 招待経由 onboarding: 続柄別に家族連携 → プロフィール判定 → home
    const isSelf = relationship === '本人';
    if (isSelf) {
      const linkedPatientId = await confirmAndLinkFamily(queryClient);
      let profileDisplayName = patientName;
      try {
        const lineProfile = await liffManager.getProfile();
        if (lineProfile?.displayName) {
          profileDisplayName = lineProfile.displayName;
        }
      } catch (profileFetchError) {
        if (profileDisplayName) {
          console.warn('LINE表示名の取得に失敗しました。利用者名を使用します:', profileFetchError);
        } else {
          console.warn(
            'LINE表示名の取得に失敗し、利用者名も未設定のためプロフィール保存をスキップします:',
            profileFetchError,
          );
        }
      }
      if (profileDisplayName) {
        try {
          await apiClient.saveUserProfile({ display_name: profileDisplayName });
          queryClient.setQueryData(['userProfile', 'check'], { completed: true });
        } catch (profileError) {
          console.error('プロフィール自動作成に失敗しました:', profileError);
          navigate('/menu/profile', { replace: true });
          return;
        }
      }
      navigate(`/?patient_id=${linkedPatientId}`, { replace: true });
    } else {
      let profileCompleted = false;
      try {
        const profileCheck = await apiClient.checkUserProfile();
        profileCompleted = profileCheck.completed;
      } catch (profileCheckError) {
        console.warn('プロフィール完了状態の確認に失敗しました:', profileCheckError);
      }

      if (profileCompleted) {
        try {
          const linkedPatientId = await confirmAndLinkFamily(queryClient);
          queryClient.setQueryData(['userProfile', 'check'], { completed: true });
          navigate(`/?patient_id=${linkedPatientId}`, { replace: true });
        } catch (linkError) {
          console.error('家族連携に失敗しました:', linkError);
          navigate('/menu/profile');
        }
      } else {
        navigate('/menu/profile');
      }
    }
  };

  return (
    <CommunicationSecretConsent
      onComplete={handleComplete}
      // Gate 強制フローでは戻るボタンを出さない（強制的に取得すべきため）。
      // 招待経由 onboarding でも戻るは提供しない（続柄選択画面へ戻る動線は LIFF の戻る操作に任せる）。
    />
  );
};

export default ConsentPage;
